import type { Client } from '@urql/core'
import { graphql } from 'app/generated/gql'
import { makeObservable, observable, action, runInAction } from 'mobx'
import type { FeatureToggle as FeatureToggleType, FeatureToggleListResult } from 'app/generated/graphql'
import { FEATURE_TOGGLE_NAMES } from 'app/generated/featureToggleNames'
import { logWarn } from 'lib/logging'
import { EntityStore } from './EntityStore'

// Shapes of the GraphQL responses this store consumes. codegen only emits
// schema types (not per-operation result types), so we describe the operation
// data locally rather than casting to `any`.
type ListData = { featureToggles: FeatureToggleListResult }
type MutationData = {
  updateFeatureToggle?: FeatureToggleType
  createFeatureToggle?: FeatureToggleType
}
type ByNameData = { featureTogglesByName: Array<Pick<FeatureToggleType, 'id' | 'name'>> }

const FEATURE_TOGGLE_FIELDS = `archived createdDate description id name type typeId updatedDate version`

const LIST_QUERY = graphql(`
  query FeatureToggles($search: String, $pagination: PaginationInput) {
    featureToggles(search: $search, pagination: $pagination) {
      hits {
        archived
        createdDate
        description
        id
        name
        type
        typeId
        updatedDate
        version
      }
      total
      page
    }
  }
`)

// Accessible to any authenticated user (the list query above is super-only).
// The API only returns toggles that apply to the caller, so a returned row
// means the toggle is enabled for them.
const BY_NAME_QUERY = graphql(`
  query FeatureTogglesByName($names: [String!]!) {
    featureTogglesByName(names: $names) {
      id
      name
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateFeatureToggle($input: CreateFeatureToggleInput!) {
    createFeatureToggle(input: $input) {
      archived
      createdDate
      description
      id
      name
      type
      typeId
      updatedDate
      version
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateFeatureToggle($id: ID!, $input: UpdateFeatureToggleInput!) {
    updateFeatureToggle(id: $id, input: $input) {
      archived
      createdDate
      description
      id
      name
      type
      typeId
      updatedDate
      version
    }
  }
`)

// `deleteFeatureToggle(id): Boolean` — added in trackable-api PR #246 (deployed
// to dev). Used by switch-off / remove-override, which delete the row.
const DELETE_MUTATION = graphql(`
  mutation DeleteFeatureToggle($id: ID!) {
    deleteFeatureToggle(id: $id)
  }
`)

export class FeatureToggleStore extends EntityStore<
  FeatureToggleType,
  FeatureToggleListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  // Per-name enabled state for the current user. Populated by `load()` from the
  // single canonical featureTogglesByName query. This is the path used app-wide
  // by `useFeatureToggle` and `<Feature>`, and works for non-super users. The
  // inherited `hits`/`list` (populated by the super-only list query) is only
  // used by the super-only Feature Toggles settings page.
  enabledByName = observable.map<string, boolean>()
  // Set once `enabledByName` has been populated by `load()`. Lets callers that
  // need to distinguish "not loaded yet" from "loaded and disabled" (e.g. route
  // guards) avoid acting on a premature `false` (TRK-5546).
  loaded = false
  // Set once the first load() request has answered, success or failure. Lets a
  // caller wait for the toggles without blocking forever when the request fails.
  loadSettled = false
  // Single in-flight/once guard: load() fetches the whole canonical list, so we
  // only ever need one request per session. Repeat callers await the same
  // promise (or return immediately once it has resolved).
  private loadPromise: Promise<void> | null = null

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: undefined,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `FeatureToggle`,
      extractList: (data) => (data as ListData).featureToggles,
      extractMutated: (data) => {
        const d = data as MutationData
        return d.updateFeatureToggle ?? d.createFeatureToggle
      },
    })
    // makeObservable (not makeAutoObservable): extends EntityStore, which already
    // calls makeObservable; a subclass annotates only its own fields here.
    makeObservable(this, {
      enabledByName: observable,
      loaded: observable,
      loadSettled: observable,
      load: action.bound,
      fetchAll: action.bound,
    })
  }

  // Fetch the user's enabled state for every known toggle in one request and
  // populate `enabledByName`, so `useFeatureToggle` reads synchronously without
  // a false→true flicker (TRK-4860). Idempotent: the first call does the work and
  // every later/concurrent caller awaits the same promise, so there's only one
  // request per session. Any name not returned by the API is recorded as
  // disabled.
  async load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise
    this.loadPromise = (async () => {
      const names = FEATURE_TOGGLE_NAMES
      const result = await this.client.query(BY_NAME_QUERY, { names: [...names] }).toPromise()
      if (result.error) {
        logWarn(`Failed to load feature toggles`, result.error.message)
        runInAction(() => {
          this.loadSettled = true
        })
        this.loadPromise = null // allow a retry on the next caller
        return
      }
      const rows = (result.data as ByNameData | undefined)?.featureTogglesByName
      const enabled = new Set((rows ?? []).map((r) => r.name))
      runInAction(() => {
        for (const name of names) this.enabledByName.set(name, enabled.has(name))
        this.loaded = true
        this.loadSettled = true
      })
    })()
    return this.loadPromise
  }

  // The settings page needs every persisted row (all Global/Workspace/User records)
  // to build both views. Pull a high page limit rather than the server default.
  async fetchAll() {
    return this.fetchList({ pagination: { limit: 1000 } })
  }

  isEnabled(name: string): boolean {
    return this.enabledByName.get(name) ?? false
  }

  reset() {
    runInAction(() => {
      super.reset()
      this.enabledByName.clear()
      this.loaded = false
      this.loadSettled = false
      this.loadPromise = null
    })
  }
}
